import { c as _c } from "react/compiler-runtime";
import { patchHostedUserVaultMutation, postHostedUserUploadByDocumentIdentifierMutation, postHostedUserVaultDecryptOptions } from '@onefootprint/fetch-hosted';
import type { FieldError } from '@onefootprint/hooks';
import type { DataIdentifier, VaultData } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CollectInvestorProfile } from '@/idv2/features/collect-investor-profile';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'collect-investor-profile-route'
});
const INVESTOR_PROFILE_FIELDS: DataIdentifier[] = ['investor_profile.employment_status', 'investor_profile.occupation', 'investor_profile.employer', 'investor_profile.annual_income', 'investor_profile.net_worth', 'investor_profile.funding_sources', 'investor_profile.investment_goals', 'investor_profile.risk_tolerance', 'investor_profile.brokerage_firm_employer', 'investor_profile.political_organization', 'investor_profile.senior_executive_symbols', 'investor_profile.family_member_names', 'investor_profile.declarations'];
export const Route = createFileRoute('/onboarding/$token/collect-investor-profile')({
  component: CollectInvestorProfilePage,
  loader: async ({
    params: {
      token
    },
    context: {
      queryClient,
      currentRequirement
    }
  }) => {
    if (currentRequirement?.kind !== 'collect_investor_profile') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    logInfo('Collect investor profile loader started', {
      requirementKind: currentRequirement.kind
    });
    const decryptedData = await queryClient.fetchQuery(postHostedUserVaultDecryptOptions({
      headers: {
        'X-Fp-Authorization': token
      },
      body: {
        fields: INVESTOR_PROFILE_FIELDS
      }
    }));
    logInfo('Collect investor profile decrypted vault data', {
      fieldsCount: Object.keys(decryptedData).length
    });
    return {
      currentRequirement,
      decryptedData
    };
  }
});
export function CollectInvestorProfilePage() {
  const $ = _c(13);
  const {
    decryptedData
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const footprint = useFootprint();
  const handleVaultError = useVaultMutationError();
  let t0;
  if ($[0] !== token) {
    t0 = patchHostedUserVaultMutation({
      headers: {
        "X-Fp-Authorization": token
      }
    });
    $[0] = token;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    mutateAsync: vaultMutation,
    isPending: isVaultPending
  } = useMutation(t0);
  let t1;
  if ($[2] !== token) {
    t1 = postHostedUserUploadByDocumentIdentifierMutation({
      headers: {
        "X-Fp-Authorization": token,
        "Content-Type": null
      }
    });
    $[2] = token;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    mutateAsync: uploadFile,
    isPending: isUploadPending
  } = useMutation(t1);
  let t2;
  if ($[4] !== footprint || $[5] !== handleVaultError || $[6] !== uploadFile || $[7] !== vaultMutation) {
    t2 = async (data, onError, files) => {
      logInfo("Collect investor profile form submission started", {
        vaultKeys: Object.keys(data).join(","),
        hasFiles: !!files?.length
      });
      ;
      try {
        await vaultMutation({
          body: data
        });
        logInfo("Collect investor profile vault mutation succeeded");
      } catch (t3) {
        const error = t3;
        const fieldErrors = handleVaultError(error, onError);
        const log = fieldErrors.length > 0 ? logWarn : logError;
        log("Collect investor profile vault mutation failed", error, {
          fieldErrorCount: fieldErrors.length
        });
        return;
      }
      if (files?.length) {
        ;
        try {
          const formData = new FormData();
          formData.append("file", files[0]);
          await uploadFile({
            path: {
              documentIdentifier: "document.finra_compliance_letter"
            },
            body: formData,
            bodySerializer: _temp
          });
          logInfo("Collect investor profile compliance letter upload succeeded");
        } catch (t4) {
          const error_0 = t4;
          handleVaultError(error_0, onError);
          logError("Collect investor profile compliance letter upload failed", error_0);
          return;
        }
      }
      footprint.navigation.forward();
    };
    $[4] = footprint;
    $[5] = handleVaultError;
    $[6] = uploadFile;
    $[7] = vaultMutation;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const onSubmit = t2;
  const t3 = isVaultPending || isUploadPending;
  let t4;
  if ($[9] !== decryptedData || $[10] !== onSubmit || $[11] !== t3) {
    t4 = <CollectInvestorProfile decryptedData={decryptedData} isLoading={t3} onSubmit={onSubmit} />;
    $[9] = decryptedData;
    $[10] = onSubmit;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp(body) {
  return body;
}